import { type ComponentPropsWithoutRef, forwardRef } from "react";

import { useTranslations } from "next-intl";

import { twMerge } from "@/utils/twMerge";

import { Icon } from "../icons/Icon";

type Props = ComponentPropsWithoutRef<"textarea"> & {
  valuePrefix?: React.ReactNode;
  keepPlaceholderOnTop?: boolean;
  onClearClick?: () => void;
};

export const FormTextarea = forwardRef<HTMLTextAreaElement, Props>(function FormTextarea(
  { className, keepPlaceholderOnTop = true, onClearClick, ...rest }: Props,
  ref,
) {
  const t = useTranslations();
  const { placeholder, value, defaultValue } = rest;

  return (
    <div className="relative w-full">
      {(defaultValue || (value && placeholder)) && keepPlaceholderOnTop && (
        <div
          className={twMerge(
            "z-10 absolute bg-white dark:bg-gray-800 translate-x-[11px] -translate-y-[11px] p-1",
            "uppercase text-xxs font-mono text-gray-500 tracking-tight",
          )}
        >
          {placeholder}
        </div>
      )}
      <textarea
        ref={ref}
        className={twMerge(
          "shadow-block rounded-xl bg-document-bg min-h-[120px] px-3.75 py-3.5 w-full h-12",
          "focus:shadow-block-selected focus:outline-none",
          "aria-invalid:shadow-input-invalid",
          "text-md tracking-tight",
          onClearClick && "pr-8",
          className,
        )}
        autoComplete="off"
        {...rest}
      />
      {(defaultValue || value) && onClearClick && (
        <button
          aria-label={t("input.clear")}
          type="button"
          className={twMerge(
            "absolute top-0 right-0 w-12 h-full",
            "hover:opacity-80",
            "active:opacity-60",
          )}
          onClick={onClearClick}
        >
          <Icon name="Cross" className="m-auto" />
        </button>
      )}
    </div>
  );
});
